import type { JSX } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useRevokeOtherSessions, useRevokeSession, useSessions } from '@/features/auth/hooks';
import { ApiError } from '@/lib/api/client';

/**
 * Account/session (device) management (Phase G-7, Job 9). Renders only
 * fields the backend actually exposes (`SessionSummary`: device label,
 * IP address, issued/expires timestamps, isCurrent) - no fabricated
 * geography, browser version, or OS detail the backend does not send.
 * Every organization role reaches this page identically; it is account-
 * level, not tenant-scoped or capability-gated.
 */
export function AccountSessionsPage(): JSX.Element {
  const { data: sessions, isLoading, isError } = useSessions();
  const revokeOne = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();

  const otherSessionCount = (sessions ?? []).filter((session) => !session.isCurrent).length;

  const mutationError =
    revokeOne.error instanceof ApiError
      ? revokeOne.error.message
      : revokeOne.error !== null
        ? 'Gagal mencabut sesi. Silakan coba lagi.'
        : revokeOthers.error instanceof Error
          ? revokeOthers.error.message
          : null;

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">Sesi &amp; Perangkat</h1>
        <p className="text-sm text-muted-foreground">Perangkat yang sedang masuk ke akun Anda.</p>
      </div>

      {isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat daftar sesi.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && sessions !== undefined && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-base">Perangkat Aktif</CardTitle>
            {otherSessionCount > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={revokeOthers.isPending}
                onClick={() => {
                  revokeOthers.mutate();
                }}
              >
                {revokeOthers.isPending ? 'Memproses...' : 'Cabut Semua Sesi Lain'}
              </Button>
            )}
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <ul className="flex flex-col gap-2">
              {sessions.map((session) => (
                <li
                  key={session.id}
                  className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {session.deviceLabel ?? 'Perangkat tidak dikenal'}
                      {session.isCurrent && (
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-normal text-secondary-foreground">
                          Perangkat ini
                        </span>
                      )}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {session.ipAddress ?? 'IP tidak diketahui'} · Masuk{' '}
                      {new Date(session.issuedAt).toLocaleString('id-ID')}
                    </span>
                  </div>
                  {!session.isCurrent && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={revokeOne.isPending}
                      onClick={() => {
                        revokeOne.mutate(session.familyId);
                      }}
                    >
                      Cabut
                    </Button>
                  )}
                </li>
              ))}
            </ul>

            {mutationError !== null && (
              <Alert variant="destructive">
                <AlertDescription>{mutationError}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
